// CASL ability factory for the feature-permission layer.
//
// RESTRICT model: everyone starts with FULL access (view + edit on every feature).
// A per-user map (see userAccess.js) then *restricts* only the features it lists:
//   { [Feature]: ['view'] }        -> that feature is view-only (edit revoked)
//   { [Feature]: [] }              -> that feature is fully denied (route hidden too)
//   (feature absent from the map)  -> unchanged, i.e. full access
// A null/undefined map means "no restrictions" — full access to everything.
//
// This matches the intent "view-only on X & Y, full access to every other area"
// without having to enumerate every feature per user.
//
// Consumed via useAbility() in feature components, e.g.
//   ability.can('edit', 'DispatchBoard')

import { AbilityBuilder, createMongoAbility } from '@casl/ability';
import { FEATURES, FEATURE_ACTIONS } from './features';

// Resolve a subject's type: an explicit tag, a Parse object's className, else the
// value itself (string feature subjects pass straight through). Lets callers also
// pass domain objects later, e.g. ability.can('edit', bookingRecord).
const detectSubjectType = (subject) => {
  if (subject && typeof subject === 'object') {
    return subject.__caslSubjectType__ || subject.className || subject.constructor?.name;
  }
  return subject;
};

export function defineAbilityFor(featurePermissions) {
  const { can, cannot, build } = new AbilityBuilder(createMongoAbility);

  // Baseline: full access (view + edit on every feature) for everyone.
  Object.values(FEATURES).forEach((feature) => {
    can(FEATURE_ACTIONS.VIEW, feature);
    can(FEATURE_ACTIONS.EDIT, feature);
  });

  // Listed user: the map RESTRICTS only the features it names. For each listed
  // feature, revoke any action NOT granted (so [] denies the feature entirely, and
  // ['view'] makes it view-only). Features absent from the map keep full access.
  // `cannot` rules are declared after the `can` baseline so they take precedence.
  if (featurePermissions) {
    Object.entries(featurePermissions).forEach(([feature, actions]) => {
      const allowed = new Set(Array.isArray(actions) ? actions : []);
      Object.values(FEATURE_ACTIONS).forEach((action) => {
        if (!allowed.has(action)) cannot(action, feature);
      });
    });
  }

  return build({ detectSubjectType });
}
